import React, { useState, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';
import { Auth0Provider } from '@auth0/auth0-react';
import { fetchConfig } from './config/runtime-config';
import logo from './logo.png';
import log from './utils/logger';

const container = document.getElementById('root');
const root = createRoot(container);

// Cleanup function for development
if (process.env.NODE_ENV === 'development') {
  const cleanup = () => {
    // Close any open connections
    if (window.socket) {
      window.socket.disconnect();
    }
    // Clear any intervals/timeouts
    const highestTimeoutId = setTimeout(() => {}, 0);
    for (let i = 0; i < highestTimeoutId; i++) {
      clearTimeout(i);
      clearInterval(i);
    }
  };
  
  window.addEventListener('beforeunload', cleanup);
  
  // Handle Ctrl+C in development
  if (typeof process !== 'undefined' && process.on) {
    process.on('SIGINT', () => {
      cleanup();
      process.exit(0);
    });
  }
}

// Loading component
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center bg-dark-bg">
    <div className="text-center">
      {/* SnipeHouse Logo with blue glow */}
      <div className="mb-8">
        <img 
          src={logo} 
          alt="SnipeHouse" 
          className="h-24 mx-auto"
          style={{ 
            filter: 'drop-shadow(0 0 20px rgba(0, 174, 239, 0.8)) drop-shadow(0 0 40px rgba(0, 174, 239, 0.5)) drop-shadow(0 0 60px rgba(0, 174, 239, 0.3))',
          }}
        />
      </div>
      
      {/* Loading spinner */}
      <div className="relative inline-block">
        <div className="w-16 h-16 border-4 border-snipehouse-blue/30 border-t-snipehouse-blue rounded-full animate-spin"></div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-8 h-8 bg-snipehouse-blue/20 rounded-full animate-pulse"></div>
        </div>
      </div>
    </div>
  </div>
);

// Error component
const ErrorScreen = ({ error, onRetry }) => (
  <div className="min-h-screen flex items-center justify-center bg-dark-bg">
    <div className="text-center max-w-md p-6">
      <div className="text-red-400 mb-4">
        <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      </div>
      <h1 className="text-2xl font-bold text-white mb-2">Configuration Error</h1>
      <p className="text-gray-400 mb-4">{error}</p>
      <button 
        onClick={onRetry}
        className="px-4 py-2 bg-snipehouse-blue text-white rounded-lg hover:bg-snipehouse-blue-dark hover:scale-105 transition-all shadow-glow-blue-soft"
      >
        Retry
      </button>
    </div>
  </div>
);

// App wrapper that fetches config before rendering
const AppWrapper = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadConfig = async () => {
    setLoading(true);
    setError(null);
    try {
      const loadedConfig = await fetchConfig();
      setConfig(loadedConfig);
    } catch (err) {
      log.error('Failed to load configuration:', err);
      setError('Having trouble connecting. Please check your internet connection and refresh the page.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  if (loading) {
    return <LoadingScreen />;
  }

  if (error) {
    return <ErrorScreen error={error} onRetry={loadConfig} />;
  }

  if (!config) {
    return <ErrorScreen error="Configuration is missing" onRetry={loadConfig} />;
  }

  return (
    <Auth0Provider
      domain={config.auth0Domain}
      clientId={config.auth0ClientId}
      authorizationParams={{
        redirect_uri: config.auth0RedirectUri || window.location.origin,
        audience: config.auth0Audience,
        scope: 'openid profile email offline_access',
      }}
      cacheLocation="localstorage"
      useRefreshTokens={true}
      useRefreshTokensSilently={true}
    >
      <App />
    </Auth0Provider>
  );
};

root.render(<AppWrapper />);
